import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  ClickAwayListener,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grow,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Tooltip,
} from '@mui/material';
import { Cell } from 'api';
import { useXlsxSpreadsheetNotesQuery } from 'components/Drawer/document-preview-drawer-tab/xlsx-viewer/useXlsxSpreadsheetNotesQuery';
import { XLSX_VIEWER_STRINGS } from 'components/Drawer/document-preview-drawer-tab/xlsx-viewer/xlsx-viewer.contants';
import { GridSkeleton } from 'components/Grid';
import WorkbookContext from 'components/ScalarSpreadsheet/utilities/WorkbookContext';
import useSpreadsheetNoteTabs from 'components/SpreadsheetNotes/SpreadsheetNoteConfig/UseSpreadsheetNoteTabs/useSpreadsheetNoteTabs';
import { TabsNavigation, Tab as TabType } from 'components/TabsNavigation';
import { useTabsMenu } from 'components/TabsNavigation/hooks/useTabsMenu';
import { XLSXViewerTabProps } from './XLSXViewerTab.props';
import { EventType, useEventSubscription } from '../../../../context';
import { useAttributeMappedWidgetDrawer } from '../../../../pages/Financials/financials-widget-drawer/AttributeMappedWidgetDrawer';
import { CellWithReferences } from '../../../../pages/Financials/types/cell-with-references';
import { ClearAttributeMappingsPayload } from '../../../../services/sheet-attribute-manager/models/sheet-attribute-manager.interface';
import { useCellMappingStore } from '../../../../store';
import adjustAlphaFromColorString from '../../../../utilities/adjust-alpha-in-color-string/adjust-alpha-from-color-string';
import { convertedColors } from '../../../ScalarSpreadsheet/constants';
import availableShortcuts, { ACTION_SHORTCUT } from '../../../ShortcutHelper/availableShortCuts';

const XLSXViewerTab = ({ document }: XLSXViewerTabProps) => {
  const { attributeMapper, attributeReferenceConfig, handlePushingAttributeMappings, handleClearMappings } =
    useAttributeMappedWidgetDrawer();
  const { updateCellMapping } = useCellMappingStore();
  const [activeTab, setActiveTab] = useState<TabType | null>(null);

  const { data, error, isLoading, isPolling } = useXlsxSpreadsheetNotesQuery(document.file.id);

  // pass notes into the store

  const notes = useMemo(() => {
    if (!data || data.length === 0) {
      return [];
    }
    return data;
  }, [data]);

  const { handleMenuOpen, handleMenuClose, menuAnchorEl } = useTabsMenu();

  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingClearPayload, setPendingClearPayload] = useState<ClearAttributeMappingsPayload>();

  const handleToggle = () => {
    setOpen(prevOpen => !prevOpen);
  };

  const handleClose = (event: Event | React.SyntheticEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }
    setOpen(false);
  };

  const isAttributeMapperEnabled = !!attributeMapper;

  const { tabs, workbookContextValue, onNoteChange, cells } = useSpreadsheetNoteTabs({
    notes,
    actionsAreDisabled: false,
    isAttributeMapperEnabled,
    attributeReferenceConfig,
    attributeMapper,
    documentId: document.document_reference_id,
  });

  const handleOpenConfirmDialog = (docRefPayload?: ClearAttributeMappingsPayload) => {
    setPendingClearPayload(docRefPayload);
    setConfirmDialogOpen(true);
    setOpen(false);
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialogOpen(false);
    setPendingClearPayload(undefined);
  };

  const handleConfirmClearMappings = () => {
    if (!handleClearMappings) return;
    handleClearMappings(pendingClearPayload);
    handleCloseConfirmDialog();
  };

  const handleActiveTabChange = (tab: TabType) => {
    setActiveTab(tab);
  };

  const clearHighlights = () => {
    if (!cells) return;

    Object.values(cells).forEach(sheet => {
      Object.values(sheet).forEach(cell => {
        cell.setCellBackgroundColor(undefined);
        // eslint-disable-next-line no-param-reassign
        cell.style = undefined;
        cell.dispatchEvent();
      });
    });
  };

  useEventSubscription(EventType.SPREADSHEET_ACTIVE_CELL_CHANGED, ({ cellInfo }: { cellInfo: CellWithReferences }) => {
    clearHighlights();
    if (!cells || !cellInfo.mappingReferences?.length) return;
    const normalizationRegex = /[^a-z0-9]/g;

    // normalize the sheet names here to use as a look up for the original sheet name, to grab cells correctly
    const normalizedToOriginal = Object.keys(cells).reduce(
      (acc, sheetName) => {
        const normalized = sheetName.toLowerCase().replaceAll(normalizationRegex, '');
        acc[normalized] = sheetName;
        return acc;
      },
      {} as Record<string, string>
    );

    cellInfo.mappingReferences.forEach(item => {
      // the sheet names are normalized outside of this context upstream
      // in order to ensure we can find the cells to highlight on the right sheet
      // the name must be normalized
      const normalizedSheetName = item.sheet_name.toLowerCase().replaceAll(normalizationRegex, '');
      // getTableName() - Look into using this - useNoteSpreadsheet is where the sheet is named
      const originalSheetName = normalizedToOriginal[normalizedSheetName];
      const noteForUpdatedSheet = originalSheetName && cells[originalSheetName];
      if (noteForUpdatedSheet) {
        item.cell_references.forEach((cellRef, index) => {
          const updatedCell = noteForUpdatedSheet?.[cellRef.ref];
          const color = convertedColors[index];
          const borderColor = adjustAlphaFromColorString(color, 0.6);
          updatedCell.setCellBackgroundColor(color);
          updatedCell.style = { boxShadow: `inset 0 0 0 3px ${borderColor}` };
          updatedCell.dispatchEvent();
        });
      }
    });
  });

  useEffect(() => {
    //   we return here to prevent updating the cell mapping when we don't
    // actually have any data. Also prevents bugs on initial load of sheet
    if (isLoading) {
      return;
    }
    // POC of propagating the cell mapping to the attribute mapper
    const cellMapping = notes
      .filter(note => note.content)
      .reduce(
        (acc, note) => {
          const { cells } = note.content;
          const name = note.name ?? '';
          acc[name] = cells ?? {};
          return acc;
        },
        {} as Record<string, Record<string, Cell>>
      );

    updateCellMapping(cellMapping);
  }, [notes, updateCellMapping, isLoading]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === 'p') {
        e.preventDefault();
        handlePushingAttributeMappings?.();
      }
    },
    [handlePushingAttributeMappings]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  if (isLoading && !error && !isPolling) {
    return <GridSkeleton />;
  }

  if (isPolling) {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight={200} gap={2}>
        <div>{XLSX_VIEWER_STRINGS.LOADING.MESSAGE}</div>
        <GridSkeleton />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <Alert severity="error" variant="filled">
          <strong>{XLSX_VIEWER_STRINGS.ERROR.TITLE}</strong>
          <div>{XLSX_VIEWER_STRINGS.ERROR.MESSAGE}</div>
        </Alert>
      </Box>
    );
  }

  // The Change engine config will be on the document here which will inform the spreadsheet note viewer
  const shortCutText = availableShortcuts.find(
    item => item.action === ACTION_SHORTCUT.PUSH_ATTRIBUTE_MAPPINGS
  )?.combination;

  return (
    <WorkbookContext.Provider value={workbookContextValue}>
      {isAttributeMapperEnabled && (
        <Box sx={{ position: 'absolute', top: 50, right: 50, zIndex: 100000 }}>
          <ButtonGroup
            variant="contained"
            color="success"
            ref={anchorRef}
            aria-label={XLSX_VIEWER_STRINGS.ARIA.SPLIT_BUTTON}>
            <Tooltip
              title={`${shortCutText?.macOS}(${XLSX_VIEWER_STRINGS.TOOLTIP.MAC}) or ${shortCutText?.windows}(${XLSX_VIEWER_STRINGS.TOOLTIP.WINDOWS})`}>
              <Button onClick={() => handlePushingAttributeMappings?.()}>
                {XLSX_VIEWER_STRINGS.BUTTON.PUSH_MAPPINGS}
              </Button>
            </Tooltip>
            <Button
              size="small"
              aria-controls={open ? XLSX_VIEWER_STRINGS.ARIA.MENU_ID : undefined}
              aria-expanded={open ? 'true' : undefined}
              aria-label={XLSX_VIEWER_STRINGS.ARIA.MENU_ACTION}
              aria-haspopup="menu"
              onClick={handleToggle}>
              <ArrowDropDownIcon />
            </Button>
          </ButtonGroup>
          <Popper sx={{ zIndex: 1 }} open={open} anchorEl={anchorRef.current} role={undefined} transition disablePortal>
            {({ TransitionProps, placement }) => (
              <Grow
                {...TransitionProps}
                style={{
                  transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
                }}>
                <Paper>
                  <ClickAwayListener onClickAway={handleClose}>
                    <MenuList id={XLSX_VIEWER_STRINGS.ARIA.MENU_ID} autoFocusItem>
                      <MenuItem onClick={() => handleOpenConfirmDialog()}>
                        {XLSX_VIEWER_STRINGS.MENU.CLEAR_ALL}
                      </MenuItem>
                      <MenuItem
                        onClick={() =>
                          handleOpenConfirmDialog({
                            documentReferenceId: document.document_reference_id,
                          })
                        }>
                        {XLSX_VIEWER_STRINGS.MENU.CLEAR_DOCUMENT}
                      </MenuItem>
                      <MenuItem
                        onClick={() =>
                          handleOpenConfirmDialog({
                            documentReferenceId: document.document_reference_id,
                            sheet: `${activeTab?.label}`,
                          })
                        }>
                        {XLSX_VIEWER_STRINGS.MENU.CLEAR_SHEET} {activeTab?.label ? `(${activeTab?.label})` : ''}{' '}
                        {XLSX_VIEWER_STRINGS.MENU.CLEAR_SHEET_SUFFIX}
                      </MenuItem>
                    </MenuList>
                  </ClickAwayListener>
                </Paper>
              </Grow>
            )}
          </Popper>
        </Box>
      )}
      <TabsNavigation
        menuAnchorEl={menuAnchorEl}
        mountOnActive
        handleMenuClose={handleMenuClose}
        handleMenuOpen={handleMenuOpen}
        relativeDrawer
        tabs={tabs}
        onActiveTabChange={handleActiveTabChange}
      />

      <Dialog open={confirmDialogOpen} onClose={handleCloseConfirmDialog}>
        <DialogTitle>{XLSX_VIEWER_STRINGS.DIALOG.TITLE}</DialogTitle>
        <DialogContent>
          <DialogContentText>{XLSX_VIEWER_STRINGS.DIALOG.CONTENT}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog} color="primary">
            {XLSX_VIEWER_STRINGS.DIALOG.CANCEL}
          </Button>
          <Button onClick={handleConfirmClearMappings} color="error" variant="contained">
            {XLSX_VIEWER_STRINGS.DIALOG.CONFIRM}
          </Button>
        </DialogActions>
      </Dialog>
    </WorkbookContext.Provider>
  );
};
export default XLSXViewerTab;
